import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Avatar from '@material-ui/core/Avatar'
import Tooltip from '@material-ui/core/Tooltip'
import IconButton from '@material-ui/core/IconButton'
import SettingsIcon from '@material-ui/icons/Settings'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { makeStyles } from '@material-ui/core/styles'
import { coachColorAt } from './academy-resource-calendar'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75, 0),
  },
  selectAllBtn: {
    fontSize: 11,
    padding: theme.spacing(0.25, 0.75),
    minWidth: 0,
    textTransform: 'none',
    lineHeight: 1.4,
    flexShrink: 0,
    marginRight: theme.spacing(0.5),
  },
  divider: {
    width: 1,
    height: 20,
    background: theme.palette.divider,
    flexShrink: 0,
    marginRight: theme.spacing(0.5),
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    borderRadius: 20,
    padding: theme.spacing(0.25, 0.75, 0.25, 0.5),
    cursor: 'pointer',
    border: '1px solid transparent',
    transition: 'opacity 0.15s, border-color 0.15s',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  chipSelected: {
    borderColor: theme.palette.divider,
    background: theme.palette.grey[50],
  },
  chipDeselected: {
    opacity: 0.45,
  },
  chipError: {
    borderColor: theme.palette.error.light,
  },
  avatar: {
    width: 22,
    height: 22,
    fontSize: 10,
    flexShrink: 0,
  },
  coachName: {
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    maxWidth: 90,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  errorIcon: {
    fontSize: 14,
    color: theme.palette.error.main,
    flexShrink: 0,
  },
  settingsBtn: {
    padding: 2,
    marginLeft: 2,
  },
  settingsIcon: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  empty: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}))

/**
 * Horizontal coach filter bar — each coach rendered as a compact color chip.
 * Click the chip to toggle visibility. Click the gear icon to open settings.
 */
function AcademyCoachSelector({
  coaches,
  coachColorMap,
  coachStates,
  selectedCoachIds,
  loading,
  onToggleCoach,
  onSelectAll,
  onDeselectAll,
  onShowSettings,
}) {
  const classes = useStyles()
  const allSelected =
    coaches.length > 0 && selectedCoachIds.size === coaches.length

  if (loading) {
    return null
  }

  if (coaches.length === 0) {
    return (
      <Typography className={classes.empty}>
        No coaches found in this academy.
      </Typography>
    )
  }

  return (
    <Box className={classes.root}>
      <Button
        className={classes.selectAllBtn}
        color="primary"
        size="small"
        onClick={allSelected ? onDeselectAll : onSelectAll}
      >
        {allSelected ? 'Deselect All' : 'Select All'}
      </Button>

      <Box className={classes.divider} />

      {coaches.map(coach => {
        const color = coachColorMap[coach.id] || coachColorAt(0)
        const photo = coach.coachProfile?.profilePhoto
        const checked = selectedCoachIds.has(coach.id)
        const loadError = coachStates?.[coach.id]?.error
        const initials = (coach.name || 'U')
          .split(' ')
          .slice(0, 2)
          .map(s => s[0])
          .join('')
          .toUpperCase()

        return (
          <Tooltip
            key={coach.id}
            title={
              loadError
                ? `Could not load calendar for ${coach.name}`
                : coach.name || ''
            }
            placement="top"
          >
            <Box
              className={`${classes.chip} ${
                checked ? classes.chipSelected : classes.chipDeselected
              } ${loadError ? classes.chipError : ''}`}
              onClick={() => onToggleCoach(coach.id)}
              role="button"
              aria-pressed={checked}
              aria-label={
                loadError
                  ? `${coach.name} (calendar failed to load)`
                  : coach.name
              }
              tabIndex={0}
              onKeyDown={e =>
                (e.key === 'Enter' || e.key === ' ') && onToggleCoach(coach.id)
              }
            >
              <Avatar
                className={classes.avatar}
                src={photo || undefined}
                style={{ background: color.bg, color: color.text }}
              >
                {!photo && initials}
              </Avatar>
              <Typography
                className={classes.coachName}
                color={checked ? 'inherit' : 'textSecondary'}
              >
                {coach.name}
              </Typography>
              {loadError && (
                <ErrorOutlineIcon
                  className={classes.errorIcon}
                  aria-hidden="true"
                />
              )}
              {onShowSettings && (
                <IconButton
                  className={classes.settingsBtn}
                  size="small"
                  onClick={e => {
                    e.stopPropagation()
                    onShowSettings(coach)
                  }}
                  aria-label={`Settings for ${coach.name}`}
                >
                  <SettingsIcon className={classes.settingsIcon} />
                </IconButton>
              )}
            </Box>
          </Tooltip>
        )
      })}
    </Box>
  )
}

AcademyCoachSelector.propTypes = {
  coaches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      coachProfile: PropTypes.shape({ profilePhoto: PropTypes.string }),
    }),
  ).isRequired,
  coachColorMap: PropTypes.object.isRequired,
  coachStates: PropTypes.object,
  loading: PropTypes.bool,
  selectedCoachIds: PropTypes.instanceOf(Set).isRequired,
  onToggleCoach: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onDeselectAll: PropTypes.func.isRequired,
  onShowSettings: PropTypes.func,
}

AcademyCoachSelector.defaultProps = {
  coachStates: {},
}

export default AcademyCoachSelector
